import { NICKNAME_MESSAGES, nicknameProblem, type ChallengeInfo } from '@cruz/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { load, save } from '../lib/storage.ts';
import { api, ApiError, errorMessage } from './api.ts';
import { clockOffset } from './clock.ts';
import { loadGame, saveGame, type DeviceGame } from './device.ts';
import { Countdown, LiveRoom, ROOM_POLL_MS, RoomList } from './LiveRoom.tsx';
import { PlayScreen } from './PlayScreen.tsx';
import { pollDelay, usePolling } from './polling.ts';
import { LiveNote, ResultsScreen } from './ResultsScreen.tsx';
import {
  inputClass,
  Leaderboard,
  Notice,
  primaryButton,
  secondaryButton,
  ShareButtons,
} from './ui.tsx';

type View = 'landing' | 'playing' | 'results';

const days = (iso: string, nowIso: string) =>
  Math.max(0, Math.ceil((Date.parse(iso) - Date.parse(nowIso)) / 86_400_000));

/** A live start seen this recently still shows the countdown and starts the game by itself. */
const AUTO_START_WINDOW_MS = 30_000;

/**
 * /reto/{code}: who made it, how it works and the leaderboard; join with a nickname;
 * then play and see the results. A device that already joined goes back to its game.
 * Live challenges add a waiting room and a common countdown before the first question.
 */
export function ChallengeScreen({ code }: { code: string }) {
  const [info, setInfo] = useState<ChallengeInfo | null>(null);
  const [game, setGame] = useState<DeviceGame | null>(() => loadGame(code));
  const [view, setView] = useState<View>('landing');
  const [error, setError] = useState<string | null>(null);
  /** Server clock − phone clock, so every phone counts down to the same instant */
  const [offset, setOffset] = useState(0);
  const [countdown, setCountdown] = useState(false);

  /** Takes a fresh ChallengeInfo; a live start that just happened opens the countdown. */
  const apply = useCallback((i: ChallengeInfo, sentAt: number) => {
    const off = clockOffset(i.server_now, sentAt, Date.now());
    setInfo(i);
    setOffset(off);
    if (
      i.mode === 'live' &&
      i.status === 'open' &&
      i.starts_at &&
      i.me?.state === 'not_started' &&
      Date.now() + off < Date.parse(i.starts_at) + AUTO_START_WINDOW_MS
    ) {
      setCountdown(true);
    }
  }, []);

  useEffect(() => {
    let live = true;
    const sentAt = Date.now();
    api
      .info(code, game?.token, { retries: 6 })
      .then((i) => {
        if (!live) return;
        apply(i, sentAt);
        setError(null);
        if (game && i.me === null) {
          // The token is not valid for this challenge any more: start afresh
          setGame(null);
        } else if (i.me?.state === 'finished') {
          setView('results');
        } else if (i.me?.state === 'playing') {
          // The game clock is running: straight back to the question
          setView('playing');
        }
      })
      .catch((err: unknown) => {
        if (live) setError(errorMessage(err));
      });
    return () => {
      live = false;
    };
  }, [code, game, apply]);

  const onFinished = useCallback(() => setView('results'), []);
  const onCountdownDone = useCallback(() => {
    setCountdown(false);
    setView('playing');
  }, []);

  const inRoom =
    info?.mode === 'live' && info.status === 'open' && !info.starts_at && view === 'landing';

  // On this page (e.g. the creator waiting for friends) the leaderboard refreshes itself;
  // in a live room, quickly, to see friends arrive and the start the moment it happens
  usePolling(
    () => {
      const sentAt = Date.now();
      return api
        .info(code, game?.token, { retries: 0 })
        .then((i) => apply(i, sentAt))
        .catch(() => {});
    },
    view !== 'landing' || countdown
      ? null
      : inRoom
        ? ROOM_POLL_MS
        : pollDelay(info && { closed: info.status === 'closed', playing: info.playing.length }),
  );

  const startForAll = useCallback(async () => {
    if (!game) return;
    const sentAt = Date.now();
    try {
      const r = await api.start(code, game.token);
      const off = clockOffset(r.server_now, sentAt, Date.now());
      setOffset(off);
      setInfo((i) => (i ? { ...i, starts_at: r.starts_at } : i));
      setCountdown(true);
    } catch (err) {
      throw new Error(errorMessage(err), { cause: err });
    }
  }, [code, game]);

  if (error && !info) {
    return (
      <div className="space-y-4">
        <Notice tone="error">{error}</Notice>
        <a href="/reto/nuevo" className={secondaryButton}>
          Crear un reto nuevo
        </a>
      </div>
    );
  }
  if (!info) {
    return (
      <p className="py-10 text-center text-lg text-ink-soft dark:text-cream/75">
        Cargando el reto…
      </p>
    );
  }
  if (view === 'playing' && game) {
    return <PlayScreen code={code} token={game.token} onFinished={onFinished} />;
  }
  if (view === 'results' && game) {
    return <ResultsScreen code={code} token={game.token} creator={info.creator} />;
  }
  if (countdown && game && info.starts_at) {
    return <Countdown startsAt={info.starts_at} offset={offset} onDone={onCountdownDone} />;
  }

  const closed = info.status === 'closed';
  const live = info.mode === 'live';
  const waitingRoom = live && !closed && !info.starts_at;
  const isCreator = game?.nickname === info.creator && info.me?.is_creator;
  const daysLeft = days(info.expires_at, info.server_now);
  return (
    <div className="space-y-7">
      <section>
        <h1 className="font-serif text-3xl leading-snug text-purple dark:text-cream">
          {isCreator
            ? waitingRoom
              ? 'Tu sala está lista'
              : 'Tu reto está listo'
            : `${info.creator} te reta`}
        </h1>
        <p className="mt-2 text-lg text-ink-soft dark:text-cream/80">
          {info.question_count} preguntas sobre la Semana Santa de Sevilla,{' '}
          {info.seconds_per_question} segundos para cada una. Datos oficiales de {info.data_year}.
        </p>
        <p className="mt-1 text-base text-ink-soft dark:text-cream/70">
          {closed
            ? 'Este reto ya ha terminado: la clasificación es definitiva.'
            : waitingRoom
              ? `Jugáis todos a la vez: cuando ${isCreator ? 'pulses «Empezar para todos»' : `${info.creator} dé la salida`}, la primera pregunta sale en todos los móviles al mismo tiempo. Luego cada uno va a su ritmo.`
              : `Se puede jugar ${daysLeft <= 1 ? 'hasta mañana' : `durante ${daysLeft} días más`}. No se puede volver atrás ni pausar.`}
        </p>
      </section>

      {waitingRoom && game && info.me?.state === 'not_started' && (
        <LiveRoom info={info} code={code} me={game.nickname} onStart={startForAll} />
      )}

      {!waitingRoom && !closed && game && info.me?.state === 'not_started' && (
        <section className="space-y-3">
          {info.me.is_creator && (
            <>
              <h2 className="font-serif text-2xl">Compártelo con tus amigos</h2>
              <ShareButtons code={code} creator={info.creator} />
            </>
          )}
          <button
            type="button"
            className={info.me.is_creator ? secondaryButton : primaryButton}
            onClick={() => setView('playing')}
          >
            {info.me.is_creator ? 'Jugar yo ahora' : 'Empezar'}
          </button>
        </section>
      )}

      {!closed && !game && (
        <>
          {waitingRoom && <RoomList room={info.room} creator={info.creator} />}
          <JoinForm
            code={code}
            label={waitingRoom ? 'Entrar en la sala' : 'Aceptar el reto'}
            onJoined={(g) => {
              saveGame(code, g);
              setGame(g);
              // In a waiting room, wait; otherwise (or a latecomer) play straight away
              if (!waitingRoom) setView('playing');
            }}
          />
        </>
      )}

      {(!waitingRoom || info.leaderboard.length > 0) && (
        <section>
          <h2 className="mb-3 font-serif text-2xl text-purple dark:text-cream">Clasificación</h2>
          <Leaderboard entries={info.leaderboard} playing={info.playing} me={game?.nickname} />
          {info.leaderboard.length + info.playing.length > 0 && (
            <LiveNote closed={closed} playing={info.playing.length} />
          )}
        </section>
      )}

      {closed && (
        <a href="/reto/nuevo" className={primaryButton}>
          Crear un reto nuevo
        </a>
      )}
    </div>
  );
}

function JoinForm({
  code,
  label,
  onJoined,
}: {
  code: string;
  label: string;
  onJoined: (g: DeviceGame) => void;
}) {
  const [nickname, setNickname] = useState(() => load('cruz.reto.nickname', ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = nickname.trim() ? nicknameProblem(nickname) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const p = nicknameProblem(nickname);
    if (p) return setError(NICKNAME_MESSAGES[p]);
    setBusy(true);
    setError(null);
    try {
      const r = await api.join(code, nickname);
      save('cruz.reto.nickname', r.nickname);
      onJoined({ token: r.token, nickname: r.nickname });
    } catch (err) {
      setError(
        err instanceof ApiError && err.body?.error === 'nickname_invalid' && err.body.reason
          ? NICKNAME_MESSAGES[err.body.reason]
          : errorMessage(err),
      );
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3">
      <label htmlFor="join-nickname" className="block text-lg font-semibold">
        Tu apodo
      </label>
      <input
        id="join-nickname"
        className={inputClass}
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        maxLength={40}
        autoComplete="nickname"
        enterKeyHint="go"
        aria-invalid={problem != null}
        aria-describedby="join-help"
      />
      <p id="join-help" className="text-sm text-ink-soft dark:text-cream/70">
        {problem ? NICKNAME_MESSAGES[problem] : 'Sin registro. Una partida por móvil.'}
      </p>
      {error && <Notice tone="error">{error}</Notice>}
      <button type="submit" className={primaryButton} disabled={busy}>
        {busy ? 'Entrando…' : label}
      </button>
    </form>
  );
}
